import React from "react";
import { htmlToText } from "../../../utils/toDisplayString";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faTrash,
  faStar,
  faPaperPlane,
  faBan,
  faNewspaper,
  faCalendarAlt,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatDate,
  statusBadgeClass,
} from "./newsUtils";

// A single news card rendered in the management grid.
const NewsCard = ({
  item,
  categories,
  onPublish,
  onUnpublish,
  onFeature,
  onEdit,
  onDelete,
  onView,
}) => {
  const cat = categories.find((c) => c.id === item.categoryId);
  const status = item.status || (item.isPublished ? "PUBLISHED" : "DRAFT");
  const coverImage = item.coverImage || item.image || null;

  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
      {/* Cover */}
      <div className="relative h-44 w-full overflow-hidden bg-slate-100">
        {coverImage ? (
          <img
            src={coverImage}
            alt={item.coverImageAlt || item.title || ""}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-100 text-slate-400">
            <FontAwesomeIcon icon={faNewspaper} className="text-4xl" />
          </div>
        )}
        {/* Top badges */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {item.featured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-0.5 text-[11px] font-bold text-amber-950 shadow">
              <FontAwesomeIcon icon={faStar} size="xs" /> Featured
            </span>
          )}
          {status === "PUBLISHED" ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-0.5 text-[11px] font-bold text-white shadow">
              <FontAwesomeIcon icon={faCheckCircle} size="xs" /> Published
            </span>
          ) : (
            <span className={statusBadgeClass(status)}>{status}</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          {cat && (
            <span className="inline-flex items-center rounded bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-600">
              {cat.name || cat.title}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faCalendarAlt} size="xs" />
            {formatDate(item.publishAt || item.createdAt)}
          </span>
        </div>

        <h3
          onClick={() => onView(item)}
          className="m-0 cursor-pointer text-[15px] font-bold leading-snug text-slate-900 line-clamp-2 hover:text-indigo-600"
        >
          {htmlToText(item.title)}
        </h3>

        {item.excerpt && (
          <p className="mt-2 mb-0 text-[13px] leading-relaxed text-slate-500 line-clamp-3">
            {htmlToText(item.excerpt)}
          </p>
        )}

        {item.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 grid grid-cols-2 items-center gap-2 border-t border-slate-100 pt-3">
          {status === "PUBLISHED" ? (
            <button
              onClick={() => onUnpublish(item)}
              className="inline-flex min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100"
            >
              <FontAwesomeIcon icon={faBan} size="xs" /> Unpublish
            </button>
          ) : (
            <button
              onClick={() => onPublish(item)}
              className="inline-flex min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
            >
              <FontAwesomeIcon icon={faPaperPlane} size="xs" /> Publish
            </button>
          )}

          <button
            onClick={() => onFeature(item)}
            className={`inline-flex min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
              item.featured
                ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <FontAwesomeIcon icon={faStar} size="xs" />
            {item.featured ? "Unfeature" : "Feature"}
          </button>

          <div className="col-span-2 ml-auto flex items-center gap-1">
            <IconBtn icon={faEye} title="View" onClick={() => onView(item)} />
            <IconBtn icon={faEdit} title="Edit" onClick={() => onEdit(item)} />
            <IconBtn icon={faTrash} title="Delete" danger onClick={() => onDelete(item)} />
          </div>
        </div>
      </div>
    </article>
  );
};

const IconBtn = ({ icon, title, onClick, danger }) => (
  <button
    onClick={onClick}
    title={title}
    className={`flex h-8 w-8 items-center justify-center rounded-lg border transition ${
      danger
        ? "border-red-200 bg-white text-red-600 hover:bg-red-50"
        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
    }`}
  >
    <FontAwesomeIcon icon={icon} size="sm" />
  </button>
);

export default NewsCard;
